import { newsroomService, formatNewsroomData } from './newsroomService';

class BlogAPIService {
  constructor() {
    this.baseURL = process.env.REACT_APP_BLOG_API_BASE_URL;
    this.imageBaseURL = process.env.REACT_APP_IMAGE_BASE_URL;
    this.cmsBaseURL = process.env.REACT_APP_API_URL;
  }

  // Fetch blogs from Strapi CMS newsroom
  async fetchBlogs() {
    try {
      // Use the new newsroom service to fetch from Strapi CMS
      const result = await newsroomService.getNewsroomPosts({
        'pagination[limit]': 1000 
      });
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch newsroom data');
      }
      
      // Transform the Strapi newsroom data to match the existing blogPosts structure
      return formatNewsroomData(result.data);
    } catch (error) {
      console.error('Error fetching blogs from newsroom:', error);
      console.error('Full error details:', error.message);
      
      // Throw error instead of returning fallback data
      throw new Error('Failed to fetch blog data from Strapi CMS');
    }
  }

  // Fetch a single blog post by ID from Strapi newsroom
  async fetchBlogById(id) {
    try {
      // Use the new newsroom service to fetch single post from Strapi CMS
      const result = await newsroomService.getNewsroomPostById(id);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch newsroom post');
      }
      
      // Import and use the single post formatter
      const { formatSingleNewsroomPost } = await import('./newsroomService');
      return formatSingleNewsroomPost(result.data);
    } catch (error) {
      console.error('Error fetching blog by ID from newsroom:', error);
      throw error;
    }
  }

  transformBlogData(apiData) {
    // Handle different possible API response structures
    let blogArray = [];
    
    if (Array.isArray(apiData)) {
      // Direct array response
      blogArray = apiData;
    } else if (apiData && Array.isArray(apiData.data)) {
      // Strapi-like structure with data property
      blogArray = apiData.data;
    } else if (apiData && Array.isArray(apiData.results)) {
      // API with results property
      blogArray = apiData.results;
    } else if (apiData && Array.isArray(apiData.items)) {
      // API with items property
      blogArray = apiData.items;
    } else {
      console.warn('Unexpected API response structure:', apiData);
      return [];
    }

    return blogArray.map((blog, index) => {
      // Handle different possible blog object structures
      const blogData = blog.attributes || blog; // Support both Strapi-like and direct structures
      
      // Special debug logging for the 3rd item (index 2)
      if (index === 2) {
        }
      
      // Extract image/video URL
      const extractedMedia = this.extractImageUrl(blogData);
      
      // Handle video objects vs regular image URLs
      let image = null, isVideo = false, videoUrl = null;
      
      if (extractedMedia && typeof extractedMedia === 'object' && extractedMedia.isVideo) {
        // This is a video
        image = null; // Don't use placeholder for video
        isVideo = true;
        videoUrl = extractedMedia.url;
      } else if (extractedMedia && 
                 !extractedMedia.includes('placeholder') && 
                 !extractedMedia.includes('/clients/')) {
        // This is a valid real image from API
        image = extractedMedia;
      } else {
        // No valid image available - leave as null
        image = null;
      }
      
      // Handle tag field - can be array of objects or simple string
      let tagValue = "News"; // default
      if (Array.isArray(blogData.tag) && blogData.tag.length > 0) {
        // If tag is an array of objects, extract fullName from first item
        const firstTag = blogData.tag[0];
        if (typeof firstTag === 'object' && firstTag.fullName) {
          tagValue = firstTag.fullName;
        } else if (typeof firstTag === 'string') {
          tagValue = firstTag;
        }
      } else if (typeof blogData.tag === 'string') {
        tagValue = blogData.tag;
      } else if (blogData.category) {
        tagValue = blogData.category;
      }

      // Handle hashtags field - ensure it's always an array of strings
      let hashtagsArray = [];
      if (Array.isArray(blogData.hashtags)) {
        hashtagsArray = blogData.hashtags.map(tag => 
          typeof tag === 'object' ? (tag.name || tag.text || String(tag)) : String(tag)
        );
      } else if (blogData.hashtags && typeof blogData.hashtags === 'string') {
        hashtagsArray = [blogData.hashtags];
      }

      const transformedBlog = {
        id: blog.id || blogData.id || `blog-${index}`,
        image: image,
        isVideo: isVideo,
        videoUrl: videoUrl,
        tag: tagValue,
        title: blogData.title,
        desc: blogData.description || blogData.excerpt || blogData.summary || "",
        post_desc: blogData.post_desc || blogData.description || blogData.content || blogData.body || "",
        overlay: true,
        publishedAt: blogData.publishedAt || blogData.published_at || blogData.created_at || new Date().toISOString(),
        slug: blogData.slug || `blog-${blog.id || index}`,
        author: typeof blogData.author === 'object' ? blogData.author?.fullName || blogData.author?.name || "BA Systems" : (blogData.author || blogData.fullName || "BA Systems"),
        hashtags: hashtagsArray,
        timeAgo: this.calculateTimeAgo(blogData.publishedAt || blogData.published_at || blogData.created_at),
      };

      // Special debug logging for the 3rd item (index 2)
      if (index === 2) {
        }
      
      return transformedBlog;
    });
  }

  // Helper function to check if a file is a video
  isVideoFile(url) {
    if (!url || typeof url !== 'string') return false;
    const videoExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv'];
    const urlLower = url.toLowerCase();
    return videoExtensions.some(ext => urlLower.includes(ext));
  }

  // Helper function to extract image URL from different possible structures
  extractImageUrl(blogData) {
    // Try different possible image field names and structures
    const possibleImageFields = [
      'postImages', // API specific field - check this first!
      'image',
      'images', 
      'featuredImage',
      'featured_image', 
      'thumbnail',
      'cover_image',
      'banner',
      'photo',
      'picture'
    ];

    for (const field of possibleImageFields) {
      const imageField = blogData[field];
      if (typeof imageField === 'string' && imageField.trim() !== '') {
        // Direct URL string - check if it needs a base URL
        let imageUrl = imageField.trim();
        
        // If it's a relative URL, prepend base URL
        if (!imageUrl.startsWith('http') && !imageUrl.startsWith('data:')) {
          // Check if it starts with /
          if (imageUrl.startsWith('/')) {
            // Use the correct image base URL for paths starting with /
            imageUrl = `${this.imageBaseURL}${imageUrl}`;
          } else {
            // If it doesn't start with /, add both base URL and /
            // Handle cases like "feedimages/9_1_1753242372749.jpg"
            imageUrl = `${this.imageBaseURL}/${imageUrl}`;
          }
        }
        
        // Check if this is a video file
        if (this.isVideoFile(imageUrl)) {
          return {
            type: 'video',
            url: imageUrl,
            isVideo: true
          };
        }
        
        return imageUrl;
      } else if (Array.isArray(imageField) && imageField.length > 0) {
        // Array of images - take the first one (index 0)
        const firstImage = imageField[0];
        if (typeof firstImage === 'string') {
          let imageUrl = firstImage.trim();
          
          // Handle relative URLs
          if (!imageUrl.startsWith('http') && !imageUrl.startsWith('data:') && imageUrl.startsWith('/')) {
            imageUrl = `${this.imageBaseURL}${imageUrl}`;
          } else if (!imageUrl.startsWith('http') && !imageUrl.startsWith('data:')) {
            // Handle cases like "feedimages/9_1_1753242372749.jpg"
            imageUrl = `${this.imageBaseURL}/${imageUrl}`;
          }
          
          // Check if this is a video file
          if (this.isVideoFile(imageUrl)) {
            return {
              type: 'video',
              url: imageUrl,
              isVideo: true
            };
          }
          
          return imageUrl;
        } else if (firstImage && firstImage.url) {
          let imageUrl = firstImage.url.trim();
          
          // Handle relative URLs
          if (!imageUrl.startsWith('http') && !imageUrl.startsWith('data:') && imageUrl.startsWith('/')) {
            imageUrl = `${this.imageBaseURL}${imageUrl}`;
          } else if (!imageUrl.startsWith('http') && !imageUrl.startsWith('data:')) {
            // Handle cases like "feedimages/9_1_1753242372749.jpg"
            imageUrl = `${this.imageBaseURL}/${imageUrl}`;
          }
          
          return imageUrl;
        } else if (firstImage && firstImage.attributes && firstImage.attributes.url) {
          let imageUrl = firstImage.attributes.url.trim();
          
          // Handle relative URLs
          if (!imageUrl.startsWith('http') && !imageUrl.startsWith('data:') && imageUrl.startsWith('/')) {
            imageUrl = `${this.imageBaseURL}${imageUrl}`;
          } else if (!imageUrl.startsWith('http') && !imageUrl.startsWith('data:')) {
            // Handle cases like "feedimages/9_1_1753242372749.jpg"
            imageUrl = `${this.imageBaseURL}/${imageUrl}`;
          }
          
          return imageUrl;
        }
      } else if (imageField && imageField.data) {
        // Strapi-like structure
        if (Array.isArray(imageField.data) && imageField.data[0]) {
          const firstImageData = imageField.data[0];
          const imageUrl = firstImageData.attributes?.url || firstImageData.url;
          if (imageUrl) {
            let processedUrl = imageUrl.trim();
            
            // Handle relative URLs
            if (!processedUrl.startsWith('http') && !processedUrl.startsWith('data:') && processedUrl.startsWith('/')) {
              processedUrl = `${this.imageBaseURL}${processedUrl}`;
            } else if (!processedUrl.startsWith('http') && !processedUrl.startsWith('data:')) {
              // Handle cases like "feedimages/9_1_1753242372749.jpg"
              processedUrl = `${this.imageBaseURL}/${processedUrl}`;
            }
            
            return processedUrl;
          }
        } else if (imageField.data.attributes) {
          let imageUrl = imageField.data.attributes.url.trim();
          
          // Handle relative URLs
          if (!imageUrl.startsWith('http') && !imageUrl.startsWith('data:') && imageUrl.startsWith('/')) {
            imageUrl = `${this.imageBaseURL}${imageUrl}`;
          } else if (!imageUrl.startsWith('http') && !imageUrl.startsWith('data:')) {
            // Handle cases like "feedimages/9_1_1753242372749.jpg"
            imageUrl = `${this.imageBaseURL}/${imageUrl}`;
          }
          
          return imageUrl;
        }
      } else if (imageField && imageField.url) {
        // Object with url property
        let imageUrl = imageField.url.trim();
        
        // Handle relative URLs
        if (!imageUrl.startsWith('http') && !imageUrl.startsWith('data:') && imageUrl.startsWith('/')) {
          imageUrl = `${this.imageBaseURL}${imageUrl}`;
        } else if (!imageUrl.startsWith('http') && !imageUrl.startsWith('data:')) {
          // Handle cases like "feedimages/9_1_1753242372749.jpg"
          imageUrl = `${this.imageBaseURL}/${imageUrl}`;
        }
        
        return imageUrl;
      }
    }
    
    return null; // Return null instead of placeholder
  }

  // LEGACY METHOD - No longer used after Strapi migration
  // TODO: Remove in future cleanup
  // Transform single blog data
  transformSingleBlogData(apiData) {
    if (!apiData) {
      return null;
    }

    // Handle different possible API response structures
    let blog = apiData;
    if (apiData.data) {
      blog = apiData.data;
    }

    const blogData = blog.attributes || blog;
    const extractedMedia = this.extractImageUrl(blogData);

    // Handle video objects vs regular image URLs
    let image = null, isVideo = false, videoUrl = null;
    
    if (extractedMedia && typeof extractedMedia === 'object' && extractedMedia.isVideo) {
      // This is a video
      image = null; // Don't use placeholder for video
      isVideo = true;
      videoUrl = extractedMedia.url;
    } else if (extractedMedia && 
               !extractedMedia.includes('placeholder') && 
               !extractedMedia.includes('/clients/')) {
      // This is a valid real image from API
      image = extractedMedia;
    } else {
      // No valid image available - leave as null
      image = null;
    }

    return {
      id: blog.id || blogData.id,
      image: image,
      isVideo: isVideo,
      videoUrl: videoUrl,
      tag: blogData.category || blogData.tag || "News",
      title: blogData.title,
      desc: blogData.description || blogData.excerpt || "",
      post_desc: blogData.post_desc || blogData.description || blogData.content || blogData.body || "",
      content: blogData.post_desc || blogData.content || blogData.body || blogData.description || "",
      overlay: true,
      publishedAt: blogData.publishedAt || blogData.published_at || blogData.created_at,
      slug: blogData.slug || `blog-${blog.id}`,
    };
  }

  // Fetch single blog post with related posts from Strapi newsroom
  async fetchSingleBlogWithRelated(postId) {
    try {
      // Import newsroom service functions
      const { formatSingleNewsroomPost, formatNewsroomData } = await import('./newsroomService');
      
      // Fetch the main post
      const mainPostResult = await newsroomService.getNewsroomPostById(postId);
      
      if (!mainPostResult.success) {
        // If specific post not found, try to get all posts and find/fallback
        console.warn(`Main post ${postId} not found, attempting fallback...`);
        const allPostsResult = await newsroomService.getNewsroomPosts({
          'pagination[limit]': 10
        });
        
        if (allPostsResult.success && allPostsResult.data.length > 0) {
          // Use first available post as fallback
          const fallbackPost = allPostsResult.data[0];
          const transformedFallbackPost = formatSingleNewsroomPost(fallbackPost);
          
          // Get related posts (exclude the fallback post)
          const relatedPosts = formatNewsroomData(allPostsResult.data.slice(1, 4)); // Take next 3 as related
          
          transformedFallbackPost.relatedPosts = relatedPosts;
          return transformedFallbackPost;
        }
        
        throw new Error(mainPostResult.error || 'Failed to fetch main newsroom post');
      }
      
      // Fetch other posts as related posts (exclude the current post)
      const relatedPostsResult = await newsroomService.getNewsroomPosts({
        'pagination[limit]': 6, // Fetch 6 posts
        'filters[id][$ne]': postId // Exclude current post
      });
      
      // Transform the main post
      const transformedMainPost = formatSingleNewsroomPost(mainPostResult.data);
      
      // Transform related posts
      const relatedPosts = relatedPostsResult.success 
        ? formatNewsroomData(relatedPostsResult.data.slice(0, 3)) // Take first 3 as related
        : [];
      
      // Add related posts to main post
      transformedMainPost.relatedPosts = relatedPosts;
      
      return transformedMainPost;
    } catch (error) {
      console.error('Error fetching single blog post with related from newsroom:', error);
      console.error('Error details:', error.message);
      console.error('Error stack:', error.stack);
      
      // As final fallback, return a basic error post structure
      return {
        id: postId || 'error',
        title: 'Post Not Available',
        desc: 'This post is currently not available. Please try again later.',
        post_desc: 'This post is currently not available. Please try again later.',
        content: 'This post is currently not available. Please try again later.',
        image: null,
        tag: 'News',
        author: 'BA Systems',
        publishedAt: new Date().toISOString(),
        relatedPosts: []
      };
    }
  }

  // LEGACY METHOD - No longer used after Strapi migration
  // TODO: Remove in future cleanup
  // Transform single blog data with related posts
  transformSingleBlogWithRelatedData(apiData) {
    if (!apiData) {
      console.error('No apiData provided to transform function');
      return null;
    }

    // Based on your sample data, the API response has a "data" array
    let mainPost;
    if (apiData.data && Array.isArray(apiData.data) && apiData.data.length > 0) {
      mainPost = apiData.data[0]; // Take the first item from the data array
      } else if (Array.isArray(apiData) && apiData.length > 0) {
      mainPost = apiData[0]; // Take the first item as the main post
      } else {
      // Handle the main blog post - try different possible field names
      mainPost = apiData.main_post || apiData.post || apiData.blog || apiData;
      }
    
    if (!mainPost) {
      console.error('No main post found in API response');
      return null;
    }

    const blogData = mainPost.attributes || mainPost;
    const extractedMedia = this.extractImageUrl(blogData);

    // Handle video objects vs regular image URLs for main post
    let image, isVideo = false, videoUrl = null;
    
    if (extractedMedia && typeof extractedMedia === 'object' && extractedMedia.isVideo) {
      // This is a video
      image = "/png/placeholder.png"; // Use placeholder image for video
      isVideo = true;
      videoUrl = extractedMedia.url;
    } else {
      // This is a regular image
      image = extractedMedia || "/png/placeholder.png";
    }

    // Transform the main blog post
    const transformedPost = {
      id: mainPost.id || blogData.id,
      image: image,
      isVideo: isVideo,
      videoUrl: videoUrl,
      tag: blogData.category || blogData.tag || "News",
      title: blogData.title ,
      desc: blogData.description || blogData.excerpt || "",
      post_desc: blogData.post_desc || blogData.description || blogData.content || blogData.body || "",
      content: blogData.post_desc || blogData.content || blogData.body || blogData.description || "",
      overlay: true,
      publishedAt: blogData.publishedAt || blogData.published_at || blogData.created_at,
      slug: blogData.slug || `blog-${mainPost.id}`,
      author: typeof blogData.author === 'object' ? blogData.author?.fullName || blogData.author?.name || "BA Systems" : (blogData.author || "BA Systems"),
      timeAgo: this.calculateTimeAgo(blogData.publishedAt || blogData.published_at || blogData.created_at),
    };

    // Handle related posts - check for different possible field names
    let relatedPostsData = [];
    
    if (apiData.data && Array.isArray(apiData.data) && apiData.data.length > 0) {
      // If apiData has a data array, related posts are within the main post object
      relatedPostsData = mainPost.relatedPost || mainPost.related_posts || mainPost.relatedPosts || mainPost.related || [];
    } else if (Array.isArray(apiData) && apiData.length > 0) {
      // If apiData is an array, related posts are within the main post object
      relatedPostsData = mainPost.relatedPost || mainPost.related_posts || mainPost.relatedPosts || mainPost.related || [];
    } else {
      // If apiData is an object, check at the top level
      relatedPostsData = apiData.relatedPost || apiData.related_posts || apiData.relatedPosts || apiData.related || [];
    }
    
    const relatedPosts = relatedPostsData.map((relatedPost, index) => {
      const relatedBlogData = relatedPost.attributes || relatedPost;
      
      // Handle related post images - they might be in imagesUrl array
      let relatedMedia = null;
      if (relatedPost.imagesUrl && Array.isArray(relatedPost.imagesUrl) && relatedPost.imagesUrl.length > 0) {
        // Use the first image from imagesUrl array
        let imageUrl = relatedPost.imagesUrl[0];
        if (!imageUrl.startsWith('http') && !imageUrl.startsWith('data:')) {
          imageUrl = `${this.imageBaseURL}/${imageUrl}`;
        }
        
        // Check if this is a video file
        if (this.isVideoFile(imageUrl)) {
          relatedMedia = {
            type: 'video',
            url: imageUrl,
            isVideo: true
          };
        } else {
          relatedMedia = imageUrl;
        }
      } else {
        // Fall back to the existing extraction method
        relatedMedia = this.extractImageUrl(relatedBlogData);
      }

      // Handle video objects vs regular image URLs for related posts
      let relatedImage, relatedIsVideo = false, relatedVideoUrl = null;
      
      if (relatedMedia && typeof relatedMedia === 'object' && relatedMedia.isVideo) {
        // This is a video
        relatedImage = "/png/placeholder.png"; // Use placeholder image for video
        relatedIsVideo = true;
        relatedVideoUrl = relatedMedia.url;
      } else {
        // This is a regular image
        relatedImage = relatedMedia || "/png/placeholder.png";
      }
      
      return {
        id: relatedPost.id || relatedBlogData.id || `related-${index}`,
        image: relatedImage,
        isVideo: relatedIsVideo,
        videoUrl: relatedVideoUrl,
        tag: relatedBlogData.category || relatedBlogData.tag || "News",
        title: relatedBlogData.title,
        desc: relatedBlogData.description || relatedBlogData.excerpt || "",
        post_desc: relatedBlogData.post_desc || relatedBlogData.description || relatedBlogData.content || relatedBlogData.body || "",
        overlay: true,
        publishedAt: relatedBlogData.publishedAt || relatedBlogData.published_at || relatedBlogData.created_at || relatedBlogData.updated_at,
        slug: relatedBlogData.slug || `blog-${relatedPost.id || index}`,
        author: typeof relatedBlogData.author === 'object' ? relatedBlogData.author?.fullName || relatedBlogData.author?.name || "BA Systems" : (relatedBlogData.author || "BA Systems"),
        timeAgo: this.calculateTimeAgo(relatedBlogData.publishedAt || relatedBlogData.published_at || relatedBlogData.created_at || relatedBlogData.updated_at),
      };
    });

    // Add related posts to the main post
    transformedPost.relatedPosts = relatedPosts;

    return transformedPost;
  }

  // Helper method to calculate time ago
  calculateTimeAgo(dateString) {
    if (!dateString) return "Recently";
    
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  }

  // Fallback data when API fails
  getFallbackBlogData() {
    return [
      {
        id: 1,
        image: "/png/placeholder.png",
        tag: "News",
        title: "BIDA Renews Service Agreement with Business Automation to....",
        desc: "BIDA has renewed its strategic partnership with Business Automation, strengthening digital transformation initiatives across government services and enhancing citizen experience through innovative technology solutions.",
        overlay: true,
      },
      {
        id: 2,
        image: "/png/placeholder.png",
        tag: "Team Activity",
        title: "Celebrating a Newlywed Colleague's Birthday",
        desc: "The aroma of vanilla and buttercream filled the office as we gathered around a magnificent birthday cake, a testament to our newlywed colleague's special day.",
        overlay: true,
      },
      {
        id: 3,
        image: "/png/placeholder.png",
        tag: "Team Activity",
        title: "Celebrating a Newlywed Colleague's Birthday",
        desc: "The aroma of vanilla and buttercream filled the office as we gathered around a magnificent birthday cake, a testament to our newlywed colleague's special day.",
        overlay: true,
      },
    ];
  }
}

// Create singleton instance
const blogAPIService = new BlogAPIService();

export default blogAPIService;
